import React             from 'react';
import Tabs              from '@widesk-ui/components/Tabs';
import View              from '@widesk-ui/components/View';
import useTheme          from '@widesk-ui/hooks/useTheme';
import Img               from '@widesk-ui/components/Img';
import translationStore  from '@widesk-ui/stores/translationStore';
import { getLocale }     from '@widesk-core/locale';
import { FormRule }      from 'antd';
import { FormItem }      from '@widesk-ui/components/form/FormItem';
import Alert             from '@widesk-ui/components/Alert';
import Button            from '@widesk-ui/components/Button';
import useForm           from '@widesk-ui/hooks/useForm';
import { CloseOutlined } from '@ant-design/icons';
import { CheckOutlined } from '@ant-design/icons';

export type TabsTools = {
	getRequiredMultiLangTabsFormRule: (lang: LanguageKey) => FormRule;
};

type Item = {
	children: ((tabsTools: TabsTools) => React.ReactNode) | React.ReactNode;
	formItems?: undefined;
} | {
	children?: undefined;
	formItems: (tabsTools: TabsTools) => React.ReactNode[];
}

export type MultiLangTabsProps = {
	items: ({ lang: LanguageKey; optional?: boolean } & Item)[];
	onChange?: (activeKey: LanguageKey) => void;
	onEditTabs?: () => void;
	childrenContainerStyle?: React.CSSProperties;
}

export default function MultiLangHorizontalTabs(props: MultiLangTabsProps) {
	const theme = useTheme();
	const form = useForm();

	const [activeKey, setActiveKey] = React.useState<LanguageKey>(getLocale());
	const [errorKeys, setErrorKeys] = React.useState<LanguageKey[]>([]);

	const optionalItemLangs = props.items.filter(item => item.optional).map(item => item.lang);
	const [renderedOptionalKeys, setRenderedOptionalKeys] = React.useState<LanguageKey[]>(optionalItemLangs);

	React.useEffect(() => {
		if (props.onChange) props.onChange(activeKey);
	}, [activeKey]);

	React.useEffect(() => {
		const translations = form.tools.getValues().translations as unknown as Partial<Record<LanguageKey, Record<string, any>>>;
		const toRenderOptionalKeys = optionalItemLangs.reduce<LanguageKey[]>((acc, lang) => {
			const translation = translations && translations[lang];
			const toRender = translation ? Object.keys(translation).some(key => {
				return translation[key] !== undefined;
			}) : false;
			if (toRender) {
				acc.push(lang);
			}
			return acc;
		}, []);

		setRenderedOptionalKeys(toRenderOptionalKeys);
	}, []);

	React.useEffect(() => {
		if (props.onEditTabs) {
			props.onEditTabs();
		}
	}, [renderedOptionalKeys]);

	const getRequiredMultiLangTabsFormRule: (lang: LanguageKey) => FormRule = (lang) => ({
		validator: (_rule, value) => {
			if (value) {
				setErrorKeys(errorKeys.filter(k => k === lang));
				return Promise.resolve();
			}

			setActiveKey(lang);
			setErrorKeys([...new Set(errorKeys), lang]);

			return Promise.reject();
		},
	});

	const tabsTools: TabsTools = {
		getRequiredMultiLangTabsFormRule,
	};

	return (
		<Tabs
			activeKey={activeKey}
			onChange={key => setActiveKey(key)}
			type="card"
			tabBarStyle={{ marginBottom: 0 }}
			items={props.items.map(item => {
				let children = typeof item.children === 'function' ? item.children(tabsTools) : item.children;
				let hasRequiredChild = false;

				if (item.formItems) {
					children = React.Children.map(item.formItems(tabsTools), child => {
						if (React.isValidElement(child)) {
							if (child.type !== FormItem) throw 'MultiLangHorizontalTabs - child is not of type FormItem';
							if (!child.props.required) return child;

							hasRequiredChild = true;

							return React.cloneElement<any>(child, {
								rules: [...(child.props.rules || []), tabsTools.getRequiredMultiLangTabsFormRule(item.lang)],
							});
						}

						return child;
					});
				}

				const isRendered = renderedOptionalKeys.includes(item.lang);

				return {
					key: item.lang,
					label: (
						<View row center>
							<LangTab lang={item.lang} unrendered={item.optional && !isRendered} />
							{hasRequiredChild && !item.optional && (
								<View color="red" size={22} lineHeight={0}>
									*
								</View>
							)}
							{item.optional && (
								<Button
									danger={isRendered}
									icon={isRendered ? <CloseOutlined /> : <CheckOutlined />}
									onClick={() => {
										if (!isRendered) {
											return setRenderedOptionalKeys([...renderedOptionalKeys, item.lang]);
										}

										const filtered = renderedOptionalKeys
											.filter(lang => item.lang !== lang);
										setRenderedOptionalKeys(filtered);
									}}
									title={isRendered ? 'Désactiver la langue' : 'Activer la langue'}
									size="small"
								/>
							)}
						</View>
					),
					children: (
						<View
							padding={theme.marginSM}
							bg={'var(--ant-color-fill-quaternary)'}
							style={{ border: `1px solid ${theme.colorBorder}` }}
						>
							{item.optional && (
								<Alert
									message="Cette langue est optionnelle ; vous pouvez activer ou désactiver la langue en cliquant sur l'icône de l'onglet"
									showIcon
								/>
							)}
							<View gap="lg" style={props.childrenContainerStyle}>
								{!item.optional || isRendered ? children : undefined}
							</View>
						</View>
					),
				};
			})}
		/>
	);
}

const LangTab = (props: { lang: LanguageKey; unrendered?: boolean }) => {
	const language = translationStore.get(props.lang);
	const theme = useTheme();

	return (
		<View
			color={props.unrendered ? theme.colorTextDescription : undefined}
			gap={10}
			row
			center
			paddingH={theme.marginSM}
		>
			<Img
				src={language?.image || ''}
				style={props.unrendered ? { filter: 'grayscale(100%)' } : undefined}
				width={22}
				preview={false}
			/>

			<div>{language?.title}</div>
		</View>
	);
};
